import { memo, useCallback, useMemo, useState } from "react";
import { Link } from "react-router";
import { ChevronRight, Scan } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/state/redux/hooks";
import {
  selectIsSelectedRecordKey,
  selectIsViewingRecordID,
  setViewingRecordFromRow,
} from "@/state/redux/slices/ui";
import { useCollections } from "@/api/hooks";
import { serializeRecord } from "@/common/record";
import { getPlural, getSingular } from "@/common/intervalTypes";
import { collectionPath } from "@/common/urls";
import { indexCollections } from "@/common/utils";
import { HGCollection, HGInterval, HGIntersectionType } from "@/common/types";
import {
  formatFittedRange,
  rangeContainsAllRecords,
  useAllRecordsTarget,
} from "@/common/allRecordsRange";
import {
  CONTENT_R,
  CONTENT_X,
  ROW_PAD_Y,
  SPINE_X,
  TITLE_CY,
} from "@/common/timelineGeometry";
import {
  BucketKind,
  GapNode,
  LevelNode,
  RecordNode,
  RecordTree,
  isLevelNode,
  safeShortName,
} from "@/common/recordTree";
import { intersectionTypeIcon } from "./IntersectionTypeIcons";
import { Spine } from "./TimelineRail";
import RecordBody, { recordCardClass } from "./RecordBody";

// --- geometry ---------------------------------------------------------------
//
// The rail itself — where the spine runs, how wide a card is inset from it —
// lives in `common/timelineGeometry.ts`, because the new-record row above the
// scroll container has to land on the same one. What is left here is the
// geometry of the levels, which nothing outside this file draws. Collapsing a
// group collapses its stretch of the spine along with its rows.

const GROUP_H = 34;
const SUB_H = 24;

const SECTION_PAGE = 50;

/** Height of the row offering to widen the range to every record in scope. */
const FIT_H = 30;

function levelHeaderHeight(depth: number) {
  // The root level has no header of its own — how many records are listed, and
  // what they are listed under, is the band's to say. So the first level header
  // parks at the very top of the scroller.
  if (depth <= 0) return 0;
  return depth === 1 ? GROUP_H : SUB_H;
}

/** Where a level's own header parks, below every ancestor header. */
function levelHeaderTop(depth: number) {
  let top = 0;
  for (let d = 0; d < depth; d++) top += levelHeaderHeight(d);
  return top;
}

function levelZ(depth: number) {
  return Math.max(40 - depth * 10, 10);
}

// --- shared parts -----------------------------------------------------------

function Chevron({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 text-muted-foreground transition-transform ${
        collapsed ? "-rotate-90" : ""
      }`}
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

// A count of one says nothing the row below it doesn't already say.
function Count({ value }: { value: number }) {
  if (value < 2) return null;
  return (
    <span className="shrink-0 font-mono text-[10.5px] tabular-nums text-ink2">
      {value.toLocaleString()}
    </span>
  );
}

/** A diamond on the spine: the marker for an interval, as opposed to a record. */
function IntervalMarker({ depth, height }: { depth: number; height: number }) {
  const size = depth === 0 ? 10 : 9;
  return (
    <span
      aria-hidden
      className="absolute rotate-45 rounded-[1px] bg-primary"
      style={{
        left: SPINE_X - size / 2,
        top: (height - size) / 2,
        width: size,
        height: size,
      }}
    />
  );
}

/** A bar across the spine: the marker for a subdivision of a group. */
function SubMarker({ height }: { height: number }) {
  return (
    <span
      aria-hidden
      className="absolute bg-node-line"
      style={{ left: SPINE_X - 4.5, top: height / 2, width: 9, height: 1 }}
    />
  );
}

// --- fit-to-all-records row -------------------------------------------------

/**
 * The offer to widen the range until every record in scope is on the timeline.
 *
 * A row rather than a glyph in the toolbar, for the same reason `RecordNew` is
 * one: it is a statement about the list — *there are records outside what you
 * are looking at* — and the list is where a reader looks for that. As an icon
 * among the view controls it was indistinguishable from the things that merely
 * rearrange the rows already on screen, and it lived in a panel that is shut by
 * default.
 *
 * It sits at the head of the timeline with its segment of the spine drawn
 * dashed, which is the same thing a gap row says one level down: the axis
 * carries on past here, and nothing from it is being shown.
 *
 * Absent once the range already takes in every record — fitted, or wider than
 * the records' span, since either way none is left off screen — and
 * absent when there is no range to fit, which is a space or collection with
 * nothing on the time axis at all.
 *
 * Absent, too, when browsing all collections: the whole space's span can reach
 * from a prehistoric record to a modern one, so fitting to it is a jump to a
 * near-useless zoom rather than an offer worth making. The row is for narrowing
 * in on one collection's records, and outside a collection there is none.
 */
function FitRangeRow({
  interval,
  endInterval,
  onNavigate,
}: {
  interval: HGInterval;
  endInterval: HGInterval | null;
  onNavigate: (interval: HGInterval, endInterval: HGInterval | null) => void;
}) {
  // Shares the cache entry `useShowAllRecordsOnBoot` and the sidebar already
  // open, so asking here costs no request of its own. It covers the collection
  // *and its descendants* — the parent only mounts this row inside a collection.
  const { target, span } = useAllRecordsTarget();

  if (!target) return null;
  if (rangeContainsAllRecords(span, interval, endInterval)) return null;

  const range = formatFittedRange(target);

  return (
    <button
      type="button"
      onClick={() => onNavigate(target.interval, target.endInterval)}
      title={`Fit the range to all records · ${range}`}
      className="group relative flex w-full items-center gap-[6px] text-left hover:bg-row-hover"
      style={{
        height: FIT_H,
        paddingLeft: CONTENT_X,
        paddingRight: CONTENT_R,
      }}
    >
      <Spine depth={0} dashed />
      {/* On the spine, in the slot a record's marker would take, and painted
          over the dashes rather than beside them. */}
      <span
        aria-hidden
        className="absolute flex items-center justify-center bg-background text-muted-foreground transition-colors group-hover:text-primary"
        style={{
          left: SPINE_X - 8,
          top: (FIT_H - 16) / 2,
          width: 16,
          height: 16,
        }}
      >
        <Scan className="size-[13px]" />
      </span>

      <span className="shrink-0 whitespace-nowrap text-[12.5px] text-muted-foreground transition-colors group-hover:text-foreground">
        Show all records
      </span>
      <span className="h-px flex-1 shrink bg-border" />
      <span className="shrink-0 truncate font-mono text-[10.5px] tabular-nums text-ink2">
        {range}
      </span>
    </button>
  );
}

// --- record row -------------------------------------------------------------

const ICON_INTERSECTIONS = new Set([
  HGIntersectionType.PartLeft,
  HGIntersectionType.PartRight,
  HGIntersectionType.Starts,
  HGIntersectionType.Ends,
  HGIntersectionType.Transit,
]);

interface RowContext {
  /** Level keys collapsed by the user; an interval level defaults to open. */
  toggled: Set<string>;
  toggle: (key: string) => void;
  expandedSections: Set<string>;
  expandSection: (key: string) => void;
  onNavigate: (interval: HGInterval) => void;
  /** The collection being browsed, if any; rows leave its badge off. */
  collectionID?: string;
  /** Collection id → its URL, for the dive-in link on a title record's row. */
  collectionPaths: Map<string, string>;
  /** Collection id → collection, so a row resolves its badges without a query. */
  collectionsByID: Map<string, HGCollection>;
}

/**
 * The way into the collection a record heads.
 *
 * Deliberately wordless and numberless. It used to say "Collection" and carry a
 * count of what had been folded into the row, and that count was read as the
 * size of the collection — which it never was: it counted only what this
 * viewport had loaded, only what fitted inside the record's span, and only what
 * no longer-spanning title record had already claimed. Opening the collection
 * then showed a different number, from a different question.
 *
 * So what is left is the affordance alone: this record has more inside it, and
 * here is the way down.
 *
 * Diving in keeps the range on screen even for a collection set to open on its
 * own (`keepRange`, read by `useFitRangeOnArrival`): the user is already looking
 * at the record, and the point of going down is to see what sits inside it
 * there, not to be thrown to wherever the whole collection spans.
 */
function CollectionDive({ path }: { path: string | undefined }) {
  // Nothing to link to means nothing to show: a label with no destination was
  // the one thing the badge was never for.
  if (!path) return null;

  return (
    <Link
      to={path}
      state={{ keepRange: true }}
      title="Open this record's collection"
      aria-label="Open this record's collection"
      // The card underneath opens the preview; the badge is a different
      // destination and must not trigger it on the way.
      onClick={(event) => event.stopPropagation()}
      className="flex size-[22px] shrink-0 self-center items-center justify-center rounded border border-primary/40 bg-card text-muted-foreground hover:border-primary hover:text-foreground"
    >
      <ChevronRight size={13} strokeWidth={2} aria-hidden />
    </Link>
  );
}

const RecordRow = memo(function RecordRow({
  node,
  depth,
  ctx,
}: {
  node: RecordNode;
  depth: number;
  ctx: RowContext;
}) {
  const dispatch = useAppDispatch();
  // Two booleans rather than the whole viewing record: with several thousand
  // rows on screen, subscribing each of them to the record itself meant every
  // preview open re-rendered the entire list.
  const selected = useAppSelector(selectIsViewingRecordID(node.record.id));
  const selectedHere = useAppSelector(selectIsSelectedRecordKey(node.key));

  const type = node.intersection.intersectionType;
  const icon = ICON_INTERSECTIONS.has(type) ? intersectionTypeIcon(type) : null;
  const dimmed = node.repeat || type === HGIntersectionType.Transit;

  // A record sitting inside its interval is a point on the line, so it gets a
  // filled dot. The ring is reserved for a record that *is* its interval.
  const contained = type === HGIntersectionType.Contained;

  // The node shrinks as the spine thins, so depth reads even out of context.
  const size = depth <= 0 ? 10 : depth === 1 ? 9 : depth === 2 ? 7 : 5;

  // This record is also a collection. Its marker stays that of any other
  // record — depth and span are what the timeline is drawing, and heading a
  // collection changes neither.
  const titleCollectionID = node.record.title_collection_id;

  return (
    <div
      className="relative"
      style={{
        paddingLeft: CONTENT_X,
        paddingRight: CONTENT_R,
        paddingTop: ROW_PAD_Y,
        paddingBottom: ROW_PAD_Y,
      }}
    >
      <Spine depth={depth} />

      {/* The card stops short of the spine, so a stub carries the mark to it.
          Drawn under the mark, which knocks out its own stretch of it. */}
      <span
        aria-hidden
        className="pointer-events-none absolute bg-node-line"
        style={{
          left: SPINE_X,
          top: TITLE_CY,
          width: CONTENT_X - SPINE_X,
          height: 1,
        }}
      />

      {icon ? (
        <div
          className="absolute flex items-center justify-center bg-background text-muted-foreground"
          style={{
            left: SPINE_X - 7.5,
            top: TITLE_CY - 7.5,
            width: 15,
            height: 15,
          }}
        >
          {icon}
        </div>
      ) : (
        <div
          className={`absolute rounded-full ${
            selected
              ? "border-[1.5px] border-primary bg-primary"
              : contained || depth >= 3
                ? "bg-muted-foreground"
                : depth <= 0
                  ? "border-2 border-primary/60 bg-background"
                  : "border-[1.5px] border-node-line bg-background"
          }`}
          style={{
            left: SPINE_X - size / 2,
            top: TITLE_CY - size / 2,
            width: size,
            height: size,
          }}
        />
      )}

      <div
        onClick={() => {
          // Only the row that opened it closes it again.
          dispatch(
            setViewingRecordFromRow(
              selected && selectedHere
                ? null
                : { record: serializeRecord(node.record), key: node.key },
            ),
          );
        }}
        title={node.repeat ? "Also shown earlier" : undefined}
        className={`relative ${recordCardClass(selected)}`}
      >
        <RecordBody
          record={node.record}
          currentCollectionID={ctx.collectionID}
          collectionsByID={ctx.collectionsByID}
          dimmed={dimmed}
          trailing={
            titleCollectionID ? (
              <CollectionDive
                path={ctx.collectionPaths.get(titleCollectionID)}
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
});

// --- capped record list -----------------------------------------------------

const RecordList = memo(function RecordList({
  sectionKey,
  records,
  depth,
  ctx,
}: {
  sectionKey: string;
  records: RecordNode[];
  depth: number;
  ctx: RowContext;
}) {
  // Without virtualization the DOM is the budget: long runs reveal in pages.
  const expanded = ctx.expandedSections.has(sectionKey);
  const shown = expanded ? records : records.slice(0, SECTION_PAGE);
  const hidden = records.length - shown.length;

  return (
    <>
      {shown.map((record) => (
        <RecordRow key={record.key} node={record} depth={depth} ctx={ctx} />
      ))}
      {hidden > 0 && (
        <div
          className="relative"
          style={{ paddingLeft: CONTENT_X, paddingRight: CONTENT_R }}
        >
          <Spine depth={depth} />
          <button
            type="button"
            onClick={() => ctx.expandSection(sectionKey)}
            className="flex h-[28px] items-center font-sans text-[11.5px] text-muted-foreground hover:text-foreground"
          >
            Show {hidden.toLocaleString()} more
          </button>
        </div>
      )}
    </>
  );
});

// --- buckets ----------------------------------------------------------------

/**
 * A bucket is an ordering, not a section. Each row already carries the glyph
 * for how it meets its interval — starts, ends, runs through — so heading the
 * runs as well would say it twice.
 */
const BucketRows = memo(function BucketRows({
  level,
  kind,
  ctx,
}: {
  level: LevelNode;
  kind: BucketKind;
  ctx: RowContext;
}) {
  const records = level.buckets[kind];
  if (records.length === 0) return null;

  return (
    <RecordList
      sectionKey={`${level.key}#${kind}`}
      records={records}
      depth={level.depth}
      ctx={ctx}
    />
  );
});

// --- gaps -------------------------------------------------------------------

const GAP_H = 24;

const GapRow = memo(function GapRow({
  gap,
  depth,
}: {
  gap: GapNode;
  depth: number;
}) {
  const unit =
    gap.count === 1 ? getSingular(gap.from.it) : getPlural(gap.from.it);
  const span =
    gap.count === 1
      ? safeShortName(gap.from)
      : `${safeShortName(gap.from)}–${safeShortName(gap.to)}`;

  return (
    <div
      className="relative flex items-center"
      style={{ height: GAP_H, paddingLeft: CONTENT_X, paddingRight: CONTENT_R }}
      title={`Nothing recorded · ${span}`}
    >
      {/* Nothing happened here, so the line goes dotted rather than absent. */}
      {/* The dashes are the indicator — a glyph on top of them would only
          compete with the subdivision markers. */}
      <Spine depth={depth} dashed />
      <span className="font-mono text-[10px] tracking-[.03em] text-muted-foreground">
        {span} · {gap.count.toLocaleString()} {unit}
      </span>
    </div>
  );
});

// --- levels -----------------------------------------------------------------

const LevelBody = memo(function LevelBody({
  level,
  ctx,
}: {
  level: LevelNode;
  ctx: RowContext;
}) {
  // Records that frame the interval open the level, its subdivisions fill the
  // middle, and records that close or outlast it come last.
  return (
    <>
      <BucketRows level={level} kind="exact" ctx={ctx} />
      <BucketRows level={level} kind="starts" ctx={ctx} />

      {level.children.map((child) =>
        isLevelNode(child) ? (
          <LevelSection key={child.key} level={child} ctx={ctx} />
        ) : (
          <GapRow key={child.key} gap={child} depth={level.depth + 1} />
        ),
      )}

      {level.leaves.length > 0 && (
        <RecordList
          sectionKey={`${level.key}#leaves`}
          records={level.leaves}
          depth={level.depth}
          ctx={ctx}
        />
      )}

      <BucketRows level={level} kind="ends" ctx={ctx} />
      <BucketRows level={level} kind="transit" ctx={ctx} />
    </>
  );
});

const LevelSection = memo(function LevelSection({
  level,
  ctx,
}: {
  level: LevelNode;
  ctx: RowContext;
}) {
  const collapsed = ctx.toggled.has(level.key);
  const depth = level.depth;
  const height = levelHeaderHeight(depth);
  const isGroup = depth === 1;

  return (
    <section className="relative">
      {/* The whole header line is the collapse target — only the label, which
          navigates, opts out of it. */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        aria-label={
          collapsed ? `Expand ${level.name}` : `Collapse ${level.name}`
        }
        onClick={() => ctx.toggle(level.key)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          ctx.toggle(level.key);
        }}
        className="group relative flex cursor-pointer items-center gap-[6px] bg-background pr-[18px] hover:bg-row-hover"
        style={{
          height,
          paddingLeft: CONTENT_X,
          position: "sticky",
          top: levelHeaderTop(depth),
          zIndex: levelZ(depth),
        }}
      >
        {/* The header is where the spine changes gauge. */}
        <Spine depth={depth} />
        {isGroup ? (
          <IntervalMarker depth={depth} height={height} />
        ) : (
          <SubMarker height={height} />
        )}

        {/* The label navigates; everything else on the line collapses. */}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            ctx.onNavigate(level.interval);
          }}
          title={`Go to ${level.name}`}
          className={
            isGroup
              ? "shrink-0 whitespace-nowrap font-serif text-[13px] font-semibold tracking-[.01em] text-accent-foreground hover:underline"
              : "shrink-0 whitespace-nowrap font-mono text-[10.5px] uppercase tracking-[.06em] text-ink2 hover:text-foreground"
          }
        >
          {isGroup ? level.name : level.shortName}
        </button>

        <span className="-my-1 shrink-0 px-[2px] py-1">
          <Chevron collapsed={collapsed} />
        </span>

        <div
          className={`h-px flex-1 shrink ${isGroup ? "bg-border" : "bg-node-line/60"}`}
        />
        <Count value={level.total} />
      </div>

      {!collapsed && <LevelBody level={level} ctx={ctx} />}
    </section>
  );
});

// --- entry point ------------------------------------------------------------

interface RecordsTimelineProps {
  tree: RecordTree;
  toggled: Set<string>;
  toggle: (key: string) => void;
  onNavigate: (interval: HGInterval, endInterval?: HGInterval | null) => void;
  /**
   * The range the *cursor* is on, which through a load is not the one the tree
   * was built for. The fit row reads it rather than `tree.root` so that pressing
   * it takes the row away at once, instead of leaving it on screen offering
   * again what is already being fetched.
   */
  interval: HGInterval;
  endInterval: HGInterval | null;
  /** The collection being browsed, if any; rows leave its badge off. */
  collectionID?: string;
}

export default function RecordsTimeline({
  tree,
  toggled,
  toggle,
  onNavigate,
  interval,
  endInterval,
  collectionID,
}: RecordsTimelineProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const collections = useCollections();

  const expandSection = useCallback(
    (key: string) =>
      setExpandedSections((previous) => new Set(previous).add(key)),
    [],
  );

  // Built once for the list rather than per row: every title record's link
  // needs a URL, and collectionPath() has to be given the whole collection.
  const collectionPaths = useMemo(
    () => new Map(collections.map((c) => [c.id, collectionPath(c)])),
    [collections],
  );
  // Likewise the index every row used to rebuild for itself, through a query
  // subscription and a filter over the whole collection list.
  const collectionsByID = useMemo(
    () => indexCollections(collections),
    [collections],
  );

  // Deliberately excludes anything that changes on selection: `ctx` is a prop
  // of every memoised row, so a new identity here re-renders the whole list.
  // Selection is read per row straight off the store instead.
  const ctx: RowContext = useMemo(
    () => ({
      toggled,
      toggle,
      expandedSections,
      expandSection,
      onNavigate,
      collectionID,
      collectionPaths,
      collectionsByID,
    }),
    [
      toggled,
      toggle,
      expandedSections,
      expandSection,
      onNavigate,
      collectionID,
      collectionPaths,
      collectionsByID,
    ],
  );

  return (
    <div className="relative pb-24">
      {/* Above the lead-in rather than below it, so the dashed stretch is the
          topmost thing on the line: the axis arriving from outside the range.
          Only inside a collection — see FitRangeRow. */}
      {collectionID && (
        <FitRangeRow
          interval={interval}
          endInterval={endInterval}
          onNavigate={onNavigate}
        />
      )}

      {/* A little air under the band, drawn as a spine segment rather than as
          padding: every segment is inset-y-0 of its own row, so padding here
          would open a gap in the line instead of extending it. */}
      <div className="relative h-[6px]">
        <Spine depth={0} />
      </div>

      <LevelBody level={tree.root} ctx={ctx} />
    </div>
  );
}
